import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { EditDxCodeData } from "../../../../../components/modals/editDxCodeSidePanel/editDxCodeSidePanel";
import { ToastMessages } from "../../../../../components/toasts/toastMessages";
import { DxCodeConfigTab } from "../../../../../../shared/enums/dxCodeConfigTab";
import { updateNccnIcdConfig } from "../../../../../redux/slice/nccnIcdConfigSlice";
import { updateNccnRegimenConfig } from "../../../../../redux/slice/nccnRegimenConfigSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { logError } from "../../../../../utils/customLogger";
import {
  buildIcdCodesForPersist,
  createPriorityConfig,
} from "../utils/tableDataTransformers";

interface UseEditDxCodeSaveParams {
  activeTab: DxCodeConfigTab;
  setIsSaving: (isSaving: boolean) => void;
  onClose: () => void;
}

/**
 * Hook to handle saving edited DX code configurations
 */
export const useEditDxCodeSave = ({
  activeTab,
  setIsSaving,
  onClose,
}: UseEditDxCodeSaveParams) => {
  const dispatch = useDispatch<AppDispatch>();

  const handleEditDxCodeSave = useCallback(
    async (data: EditDxCodeData) => {
      setIsSaving(true);
      try {
        // Transform dxCodes array into priority_config_org_specific structure
        const priorityConfig = createPriorityConfig(data.dxCodes);

        // Build the update object with org-specific priority config
        const updates: any = {
          [`priority_config_org_specific.${data.selectedOrgId}`]:
            priorityConfig,
        };

        if (activeTab === DxCodeConfigTab.DRUG) {
          const icd_codes = buildIcdCodesForPersist(data.dxCodes);

          updates.icd_codes = icd_codes;
          updates.total_codes = icd_codes.length;

          // For drugs, also save the drugType to the category field
          if (data.drugType) {
            updates.category = data.drugType;
          }
          await dispatch(updateNccnIcdConfig(data.id, updates));
        } else {
          const icdcodes = buildIcdCodesForPersist(data.dxCodes);

          updates.icdcodes = icdcodes;
          await dispatch(updateNccnRegimenConfig(data.id, updates));
        }

        toast.success(ToastMessages.DX_CODE_CONFIG_SAVE_SUCCESS);
        onClose();
      } catch (error) {
        logError(error as Error, "useEditDxCodeSave - handleEditDxCodeSave");
        toast.error(ToastMessages.DX_CODE_CONFIG_SAVE_ERROR);
      } finally {
        setIsSaving(false);
      }
    },
    [activeTab, dispatch, setIsSaving, onClose],
  );

  return handleEditDxCodeSave;
};
